import { ActivityOptions, ConferenceOptions, Reservation, Task } from "twilio-taskrouter";
import type { Call } from "@twilio/voice-sdk";
import { merge } from "lodash";
import { FlexCall } from "@twilio/flex-sdk";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import type { TaskRouter } from "~/packages/taskrouter/TaskRouter";
import type { Cbm } from "~/packages/cbm/Cbm";
import { CbmImpl } from "~/packages/cbm/CbmImpl";
import { Actions } from "./Actions";
import { ContextManager } from "~/modules/contextManager/ContextManager";
import {
    getDefaultCallerID,
    getDefaultQueueSid,
    getDefaultWorkflowSid,
    getReservation,
    hasVoiceTaskWithStatus,
    isCallTask,
    isOutboundCallingEnabled,
    isOutboundCallPending,
    isWorkerOffline
} from "./ActionUtils";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { featuresProviderImpl } from "~/modules/config/FeaturesProvider/featuresProviderImpl/featuresProviderImpl";
import type { FeaturesConfig } from "~/modules/config/FeaturesProvider/FeaturesProvider";
import { Session } from "~/modules/session";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import { FlexCallImpl } from "~/modules/FlexCall/FlexCallImpl";
import { VoiceControllerImpl } from "~/modules/voice/VoiceControllerImpl";
import { VoiceController } from "~/modules/voice/VoiceController";

export class ActionsImpl implements Actions {
    readonly #taskRouter: TaskRouter;

    readonly #cbm: Cbm;

    readonly #voiceController: VoiceController;

    readonly #flexCall: FlexCallImpl;

    readonly #accountConfig: AccountConfigDataContainer;

    #featuresConfig: FeaturesConfig;

    readonly #featuresProvider: (token: string) => Promise<FeaturesConfig>;

    readonly #session: Session;

    readonly #logger: Logger;

    constructor(ctx: ContextManager) {
        this.#flexCall = ctx.getInstanceOf(FlexCallImpl);
        this.#taskRouter = ctx.getInstanceOf(TaskRouterImpl);
        this.#cbm = ctx.getInstanceOf(CbmImpl);
        this.#voiceController = ctx.getInstanceOf(VoiceControllerImpl);
        this.#accountConfig = ctx.getInstanceOf(AccountConfigDataContainer);
        this.#session = ctx.getInstanceOf(SessionImpl);
        this.#featuresProvider = featuresProviderImpl(ctx);
        this.#logger = getLogger(LoggerName.Actions);
        this.#logger.debug("Actions constructed");
    }

    private async getFeature(flag: string): Promise<boolean> {
        if (!this.#featuresConfig) {
            this.#featuresConfig = await this.#featuresProvider(this.#session.token);
        }

        return Boolean(this.#featuresConfig.features.find((item) => item.id === flag)?.enabled);
    }

    



    async startOutboundCall(
        toNumber: string,
        fromNumber?: string,
        workflowSid?: string,
        taskQueueSid?: string,
        attributesForTaskCreation: object = {}
    ): Promise<FlexCall> {
        this.#logger.debug(`startOutboundCall invoked to number: ${toNumber}`);

        const worker = this.#taskRouter.worker;

        if (!worker) {
            const errorMsg = `startOutboundCall: worker is not initialized`;
            this.#logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (!toNumber) {
            const errorMsg = `startOutboundCall: toNumber is a required parameter`;
            this.#logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const fromNumberParam = fromNumber || getDefaultCallerID(this.#accountConfig.get().outboundCallFlows);
        if (!fromNumberParam) {
            const errorMsg = `startOutboundCall: fromNumber is required`;
            this.#logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const taskQueueSidParam = taskQueueSid || getDefaultQueueSid(this.#accountConfig.get().outboundCallFlows);
        if (!taskQueueSidParam) {
            const errorMsg = `startOutboundCall: taskQueueSid is required`;
            this.#logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        const workflowSidParam = workflowSid || getDefaultWorkflowSid(this.#accountConfig.get().outboundCallFlows);
        if (!workflowSidParam) {
            const errorMsg = `startOutboundCall: workflowSid is required`;
            this.#logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (isWorkerOffline(worker, this.#accountConfig.get().taskrouterOfflineActivitySid)) {
            const errorMsg = `startOutboundCall: worker is offline, outbound call cancelled`;
            this.#logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (!isOutboundCallingEnabled(this.#accountConfig.get().outboundCallFlows)) {
            const errorMsg = `startOutboundCall: Outbound calling is disabled in Flex account configuration, 
                                outbound call cancelled`;
            this.#logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        
        if (isOutboundCallPending()) {
            const errorMsg = `startOutboundCall: Another outbound call is already pending, outbound call cancelled`;
            this.#logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (hasVoiceTaskWithStatus(worker, "pending")) {
            const errorMsg = `startOutboundCall: Inbound call is pending, outbound call cancelled`;
            this.#logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (hasVoiceTaskWithStatus(worker, "accepted")) {
            const errorMsg = `startOutboundCall: Another voice task is already in accepted status, 
                                outbound call cancelled`;
            this.#logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        if (!this.#voiceController.isAudioInputDeviceAvailable()) {
            const errorMsg = `startOutboundCall: no audio input device, outbound call cancelled`;
            this.#logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }

        
        return new Promise(async (resolve, reject) => {
            let timeoutId: number | NodeJS.Timeout;
            const handleIncomingCall = (call: Call) => {
                this.#logger.info(
                    "Incoming call event received in startOutboundCall",
                    call?.parameters?.From,
                    call?.parameters?.To
                );
                if (call?.parameters?.From === fromNumberParam) {
                    this.#logger.debug("From phone numbers match");
                } else {
                    this.#logger.error(
                        "Incoming call is coming from a different number, not from the one given as an argument to startOutboundCall action"
                    );
                }

                clearTimeout(timeoutId);
                this.#voiceController.unsubscribeFromIncomingCallEvent(handleIncomingCall);

                this.#flexCall.init(call);

                resolve(this.#flexCall);
            };

            this.#voiceController.subscribeToIncomingCallEvent(handleIncomingCall);

            timeoutId = setTimeout(() => {
                this.#voiceController.unsubscribeFromIncomingCallEvent(handleIncomingCall);
                const errorMsg = `Timeout: No incoming call event received within 30 seconds`;
                this.#logger.error(errorMsg);
                reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
            }, 30000);

            worker.on("reservationCreated", async (reservation: Reservation) => {
                const conferenceCreated = await reservation.conference();
                this.#logger.debug("Conference created", conferenceCreated);
            });

            try {
                const outboundAttributes = { ...attributesForTaskCreation, direction: "outbound" };
                const options = {
                    taskChannelUniqueName: "voice",
                    attributes: outboundAttributes
                };

                await worker.createTask(toNumber, fromNumberParam, workflowSidParam, taskQueueSidParam, options);
            } catch (error) {
                clearTimeout(timeoutId);
                this.#voiceController.unsubscribeFromIncomingCallEvent(handleIncomingCall);
                reject(error);
            }
        });
    }

    



    async acceptTask(taskSid: string, conferenceOptions?: ConferenceOptions): Promise<Reservation> {
        this.#logger.debug(`acceptTask invoked with taskSid: ${taskSid}`);

        const worker = this.#taskRouter.worker;

        if (!worker) {
            const errorMsg = "acceptTask: worker is not initialized";
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const reservation = getReservation(worker, taskSid);

        if (!reservation) {
            const errorMsg = `acceptTask: Reservation for task ${taskSid} not found`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (reservation.status !== "pending") {
            const errorMsg = `acceptTask: Reservation ${reservation.sid} is not in pending state`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!reservation.task) {
            const errorMsg = `acceptTask: Reservation does not have a task ${taskSid}`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!isCallTask(reservation.task)) {
            if (!this.#cbm) {
                const errorMsg = "acceptTask: CBM SDK is not initialized";
                this.#logger.error(errorMsg);
                return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
            }

            try {
                return await this.#cbm.acceptReservation(reservation);
            } catch (error) {
                const errorMsg = `acceptTask: Failed to accept reservation ${reservation.sid}`;
                this.#logger.error(errorMsg);
                return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
            }
        }
        try {
            await reservation.conference(conferenceOptions);
            return reservation;
        } catch (e) {
            const errorMsg = `acceptTask: Failed to create conference for ${reservation.sid}`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }

    



    async rejectTask(taskSid: string): Promise<Reservation> {
        this.#logger.debug(`rejectTask invoked with taskSid: ${taskSid}`);

        const worker = this.#taskRouter.worker;

        if (!worker) {
            const errorMsg = "rejectTask: worker is not initialized";
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const reservation = getReservation(worker, taskSid);

        if (!reservation) {
            const errorMsg = `rejectTask: Reservation for task ${taskSid} not found`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (reservation.status !== "pending") {
            const errorMsg = `rejectTask: Reservation ${reservation.sid} is not in pending state`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!reservation.task) {
            const errorMsg = `rejectTask: Reservation does not have a task ${taskSid}`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        try {
            await this.#cbm.rejectReservation(reservation);
            return reservation;
        } catch (error) {
            const errorMsg = `rejectTask: Failed to reject reservation ${reservation.sid}`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }

    async setAttributes(attributes: Record<any, any>, mergeExisting?: boolean): Promise<void> {
        const worker = this.#taskRouter.worker;

        if (!worker) {
            const errorMsg = "setAttributes: worker is not initialized";
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const newAttributes = mergeExisting ? merge(this.#taskRouter.worker.attributes, attributes) : attributes;

        try {
            await this.#taskRouter.worker.setAttributes(newAttributes);
            return Promise.resolve();
        } catch (error) {
            const errorMsg = `setAttributes: Could not set worker attributes: ${error.message}`;
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }

    



    async setCurrentActivity(activitySid: string, activityUpdateOptions?: ActivityOptions): Promise<void> {
        this.#logger.debug(`setCurrentActivity invoked with activitySid: ${activitySid}`);

        const worker = this.#taskRouter.worker;

        if (!worker) {
            const errorMsg = "setCurrentActivity: worker is not initialized";
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const activity = worker.activities.get(activitySid);
        const workerActivity = worker.activity;

        if (activitySid === workerActivity?.sid) {
            const warnMsg = `setCurrentActivity: Activity with sid ${activitySid} is already set`;
            this.#logger.warn(warnMsg);
            return Promise.resolve();
        }

        try {
            await activity?.setAsCurrent(activityUpdateOptions);
            return Promise.resolve();
        } catch (error) {
            const response = error?.message;

            let errorMsg: string;

            if (
                !!response &&
                response.match(/Worker WK.* cannot have its activity updated while it has .* pending reservations/)
            ) {
                errorMsg = `setCurrentActivity: Failed to set current activity to activity ${activitySid} due to \
pending reservations`;
            } else {
                errorMsg = `setCurrentActivity: Failed to set current activity to activity ${activitySid}`;
            }

            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }

    async wrapupTask(taskSid: string): Promise<Reservation> {
        this.#logger.debug(`wrapupTask invoked with taskSid: ${taskSid}`);

        const worker = this.#taskRouter.worker;

        if (!worker) {
            const errorMsg = "wrapupTask: worker is not initialized";
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!this.#cbm) {
            const errorMsg = "wrapupTask: CBM SDK is not initialized";
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const reservation = getReservation(worker, taskSid);

        if (!reservation) {
            const errorMsg = `wrapupTask: Reservation for task ${taskSid} is not found`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (reservation.status !== "accepted") {
            const errorMsg = `wrapupTask: Reservation ${reservation.sid} is not in accepted state`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!reservation.task) {
            const errorMsg = `wrapupTask: Reservation does not have a task ${taskSid}`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        try {
            return await this.#cbm.wrapReservation(reservation);
        } catch (error) {
            const errorMsg = `wrapupTask: Failed to wrap-up reservation ${reservation.sid}`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }

    async completeTask(taskSid: string): Promise<Reservation> {
        this.#logger.debug(`completeTask invoked with taskSid: ${taskSid}`);

        const worker = this.#taskRouter.worker;

        if (!worker) {
            const errorMsg = "completeTask: worker is not initialized";
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!this.#cbm) {
            const errorMsg = "completeTask: CBM SDK is not initialized";
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const reservation = getReservation(worker, taskSid);

        if (!reservation) {
            const errorMsg = `completeTask: Reservation for task ${taskSid} not found`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (reservation.status === "completed") {
            const errorMsg = `completeTask: Reservation ${reservation.sid} is in completed state`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        if (!reservation.task) {
            const errorMsg = `completeTask: Reservation does not have a task ${taskSid}`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        try {
            return await this.#cbm.completeReservation(reservation);
        } catch (error) {
            const errorMsg = `completeTask: Failed to complete reservation ${reservation.sid}`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }

    async setTaskAttributes(taskSid: string, attributes: Record<any, any>, mergeExisting?: boolean): Promise<Task> {
        this.#logger.debug(`setTaskAttributes invoked with taskSid: ${taskSid}`);

        const worker = this.#taskRouter.worker;

        if (!worker) {
            const errorMsg = "setTaskAttributes: worker is not initialized";
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        const task = getReservation(worker, taskSid)?.task;

        if (!task) {
            const errorMsg = `setTaskAttributes: task for taskSid: ${taskSid} is not found`;
            this.#logger.error(errorMsg);
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }

        try {
            const newAttributes = mergeExisting ? merge(task?.attributes, attributes) : attributes;
            await task.setAttributes(newAttributes);
            return task;
        } catch (error) {
            const errorMsg = `setTaskAttributes: Could not set task attributes: ${error.message}`;
            return Promise.reject(new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg));
        }
    }
}
